import React, { useState } from "react";
import { Table, Button, Modal, Form } from "react-bootstrap";
import { format } from "date-fns";
import { PDFDocument, rgb, StandardFonts } from "pdf-lib";
import { saveAs } from "file-saver";
import ToPeso from "../components/misc/ToPeso";

const numberToWords = (num) => {
  const belowTwenty = [
    "Zero", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine",
    "Ten", "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen",
    "Seventeen", "Eighteen", "Nineteen"
  ];
  const tens = [
    "", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"
  ];
  const thousands = ["", "Thousand", "Million", "Billion"];

  if (num === 0) return "<b>Zero</b>";

  let word = "";

  const convertChunk = (n) => {
    let chunk = "";
    if (n >= 100) {
      chunk += belowTwenty[Math.floor(n / 100)] + " Hundred ";
      n %= 100;
    }
    if (n >= 20) {
      chunk += tens[Math.floor(n / 10)] + " ";
      n %= 10;
    }
    if (n > 0) {
      chunk += belowTwenty[n] + " ";
    }
    return chunk.trim();
  };

  // Separate integer and decimal parts
  const [integerPart, decimalPart] = num.toString().split(".");

  let i = 0;
  let intNum = parseInt(integerPart, 10);

  // Convert integer part
  while (intNum > 0) {
    const chunk = intNum % 1000;
    if (chunk > 0) {
      word = convertChunk(chunk) + (thousands[i] ? " " + thousands[i] : "") + " " + word;
    }
    intNum = Math.floor(intNum / 1000);
    i++;
  }

  word = word.trim();

  // Handle decimal part
  if (decimalPart) {
    const decimalNumber = parseInt(decimalPart.padEnd(2, '0').slice(0, 2), 10); // Ensure two digits for cents
    const decimalWords = decimalNumber > 0
      ? convertChunk(decimalNumber)
      : "Zero";
    word += ` and ${decimalWords} Cents`;
  }

  // Wrap each word with <b></b>
  return word;
};

// React Component
function DocumentVoucher({ applicantDetails, loanProcessingDetails }) {
    
    const [showModal, setShowModal] = useState(false);
    const [currentLoan, setCurrentLoan] = useState(null);
    const [formData, setFormData] = useState({
        otherCharges: "",
    });

  if (!applicantDetails) {
    return null;
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = () => {
    // Ensure all fields are filled
    if (Object.values(formData).some((value) => value.trim() === "")) {
      alert("All fields are required!");
      return;
    }

    // Close modal and generate PDF
    setShowModal(false);
    generatePDF(currentLoan, formData);
  };

  const openModal = (loan) => {
    setCurrentLoan(loan);
    setShowModal(true);
  };

  const generatePDF = async (loan, additionalFields) => {
    const existingPdfBytes = await fetch("/pdftemplates/voucher.pdf").then(res =>
        res.arrayBuffer()
    );

    // Load PDF with pdf-lib
    const pdfDoc = await PDFDocument.load(existingPdfBytes);

    // Embed fonts
    const helveticaFont = await pdfDoc.embedFont(StandardFonts.Helvetica);
    const helveticaFontBold = await pdfDoc.embedFont(StandardFonts.HelveticaBold);

    // Get the first page
    const pages = pdfDoc.getPages();
    const firstPage = pages[0];

    // borrower name
    firstPage.drawText(`${applicantDetails.firstName.toUpperCase()} ${applicantDetails.middleName.toUpperCase() || ""} ${applicantDetails.lastName.toUpperCase()}`, {
      x: 94,
      y: 657,
      size: 9,
      font: helveticaFontBold, 
      color: rgb(0, 0, 0)
    });

    firstPage.drawText(`${applicantDetails.firstName.toUpperCase()} ${applicantDetails.middleName.toUpperCase() || ""} ${applicantDetails.lastName.toUpperCase()}`, {
      x: 424,
      y: 329,
      size: 9,
      font: helveticaFontBold, 
      color: rgb(0, 0, 0)
    });

    //date granted

    firstPage.drawText(`${format(new Date(loan.dateGranted), "MM/dd/yyyy")}`, {
      x: 377,
      y: 642,
      size: 9,
      font: helveticaFontBold, 
      color: rgb(0, 0, 0)
    });

    //loan id

    firstPage.drawText(`${loan.id.toString().padStart(6, '0') || "N/A"}`, {
      x: 377,
      y: 657,
      size: 9,
      font: helveticaFontBold, 
      color: rgb(0, 0, 0)
    });

    firstPage.drawText(`${loan.id.toString().padStart(6, '0') || "N/A"}`, {
      x: 250,
      y: 587,
      size: 9,
      font: helveticaFontBold, 
      color: rgb(0, 0, 0)
    });

    //principal

    firstPage.drawText(`${parseFloat(loan.amountGranted).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`, {
      x: 250,
      y: 575,
      size: 9,
      font: helveticaFontBold, 
      color: rgb(0, 0, 0)
    });

    // Add additional fields from the popup form
    const { clientsFund, otherCharges } = additionalFields;
    const {serviceFee, insurance, docStamp, notarialFee} = loan;
    const additionalFieldsSum = (parseFloat(serviceFee) + parseFloat(insurance) + parseFloat(docStamp) + parseFloat(notarialFee) + parseFloat((loan.amountGranted * (loan.interestRate / 100))) + parseFloat(clientsFund) + parseFloat(otherCharges));
    const netProceeds = (loan.amountGranted - additionalFieldsSum);

    // Draw additional fields
    firstPage.drawText(`${parseFloat(serviceFee).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}`, {
      x: 250,
      y: 550,
      size: 9,
      font: helveticaFont,
      color: rgb(0, 0, 0),
    });

    firstPage.drawText(`${parseFloat(insurance).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}`, {
      x: 250,
      y: 538,
      size: 9,
      font: helveticaFont,
      color: rgb(0, 0, 0),
    });

    firstPage.drawText(`${parseFloat(docStamp).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}`, {
      x: 250,
      y: 526,
      size: 9,
      font: helveticaFont,
      color: rgb(0, 0, 0),
    });

    firstPage.drawText(`${parseFloat(notarialFee).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}`, {
      x: 250,
      y: 514,
      size: 9,
      font: helveticaFont,
      color: rgb(0, 0, 0),
    });

    firstPage.drawText(`${parseFloat((loan.amountGranted * (loan.interestRate / 100))).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}`, {
      x: 250,
      y: 502,
      size: 9,
      font: helveticaFont,
      color: rgb(0, 0, 0),
    });

    firstPage.drawText(`${parseFloat(clientsFund).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}`, {
      x: 250,
      y: 490,
      size: 9,
      font: helveticaFont,
      color: rgb(0, 0, 0),
    });

    firstPage.drawText(`${parseFloat(otherCharges).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}`, {
      x: 250,
      y: 478,
      size: 9,
      font: helveticaFont,
      color: rgb(0, 0, 0),
    });

    // Net Proceeds Php
    firstPage.drawText(`${parseFloat(netProceeds).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}`, {
        x: 484,
        y: 439,
        size: 9,
        font: helveticaFontBold,
        color: rgb(0, 0, 0),
    });

    firstPage.drawText(`${parseFloat(netProceeds).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}`, {
      x: 295,
      y: 414,
      size: 9,
      font: helveticaFontBold,
      color: rgb(0, 0, 0),
  });

    // num to words
    
    firstPage.drawText(`${numberToWords(netProceeds)} Pesos Only`, {
        x: 41,
        y: 391,
        size: 9,
        font: helveticaFontBold,
        color: rgb(0, 0, 0),
    });

    // Save the modified PDF
    const pdfBytes = await pdfDoc.save();

    // Use FileSaver to download the PDF
    saveAs(new Blob([pdfBytes]), `Voucher_${applicantDetails.firstName.toUpperCase()}_${applicantDetails.lastName.toUpperCase()}_${loan.id}.pdf`);
  };

  return (
    <>
      <h3>Applicant Details</h3>
      <Table className="list-container" hover responsive>
        <tbody>
          <tr>
            <td><strong>Full Name</strong></td>
            <td>{`${applicantDetails.firstName} ${applicantDetails.middleName || ""} ${applicantDetails.lastName}`}</td>
          </tr>
          <tr>
            <td><strong>Address</strong></td>
            <td>{`${applicantDetails.street}, ${applicantDetails.barangay}, ${applicantDetails.municipality}, ${applicantDetails.province}`}</td>
          </tr>
        </tbody>
      </Table>
  
      <h3>Loan Processing Details</h3>
      {loanProcessingDetails.length > 0 ? (
        <Table className="list-container" hover responsive>
          <thead>
            <tr>
              <th>Type</th>
              <th>Amount Granted</th>
              <th>Date Granted</th>
              <th>Interest Rate</th>
              <th>Loan Term</th>
              <th>Payment Type</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loanProcessingDetails.map((loan, index) => (
              <tr key={index}>
                <td>{loan.loanProcessingType}</td>
                <td><ToPeso amount={parseFloat(loan.amountGranted).toFixed(2)} /></td>
                <td>{format(new Date(loan.dateGranted), "MM/dd/yyyy")}</td>
                <td>{loan.interestRate}%</td>
                <td>{loan.loanTerm} months</td>
                <td>{loan.paymentType}</td>
                <td>
                  <Button variant="primary" onClick={() => openModal(loan)}>
                    Generate Voucher
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      ) : (
        <p>No loan processing details found for this applicant.</p>
      )}
  
      {/* Modal for additional fields */}
      <Modal show={showModal} onHide={() => setShowModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Enter Additional Fields</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group>
              <Form.Label>Clients Fund</Form.Label>
              <Form.Control
                type="number"
                name="clientsFund"
                value={formData.clientsFund}
                onChange={handleInputChange}
                required
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>Other Charges</Form.Label>
              <Form.Control
                type="number"
                name="otherCharges"
                value={formData.otherCharges}
                onChange={handleInputChange}
                required
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSubmit}>
            Submit
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );  
}

export default DocumentVoucher;
